import {useCallback, useMemo} from 'react';
import {
  useCreateUserPreferenceMutation,
  useDeleteUserPreferenceMutation,
  useUpdateUserPreferenceMutation,
} from 'services/userPreferences';
import type {ShortcutCommand} from '../types';
import {mapShortcut} from '../mappers';
import {useShortcutUserPreference} from './useShortcutUserPreference';

export const useShortcutUserPreferenceMutation = (
  command: ShortcutCommand,
  defaultValue: string | undefined,
) => {
  const {userPreference} = useShortcutUserPreference(command, defaultValue);
  const [
    createUserPreference,
    {isLoading: isCreating, isError: isCreateError, isSuccess: isCreateSuccess},
  ] = useCreateUserPreferenceMutation();
  const [
    updateUserPreference,
    {isLoading: isUpdating, isError: isUpdateError, isSuccess: isUpdateSuccess},
  ] = useUpdateUserPreferenceMutation();
  const [
    deleteUserPreference,
    {isLoading: isDeleting, isError: isDeleteError, isSuccess: isDeleteSuccess},
  ] = useDeleteUserPreferenceMutation();

  const {isLoading, isError, isSuccess} = useMemo(() => {
    return {
      isLoading: isCreating || isUpdating || isDeleting,
      isError: isCreateError || isUpdateError || isDeleteError,
      isSuccess: isCreateSuccess || isUpdateSuccess || isDeleteSuccess,
    };
  }, [
    isCreateError,
    isCreateSuccess,
    isCreating,
    isDeleteError,
    isDeleteSuccess,
    isDeleting,
    isUpdateError,
    isUpdateSuccess,
    isUpdating,
  ]);

  const onMutate = useCallback(
    (newShortcut: Set<string>) => {
      const newValue = mapShortcut(newShortcut);
      if (userPreference && userPreference?.id) {
        if (newShortcut.size === 0 || newValue === defaultValue) {
          deleteUserPreference(userPreference.id);
        } else {
          updateUserPreference({
            ...userPreference,
            id: userPreference.id,
            value: newValue,
          });
        }
      } else {
        createUserPreference({
          name: command,
          value: newValue,
          category: 'shortcut',
          type: 'USER_SPECIFIC',
        });
      }
    },
    [
      command,
      createUserPreference,
      defaultValue,
      deleteUserPreference,
      updateUserPreference,
      userPreference,
    ],
  );

  return {
    isLoading,
    isError,
    isSuccess,
    onMutate,
  };
};
